import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, useIsFetching } from 'react-query';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { playerAPI, dashboardAPI, matchChatAPI, authAPI, teamsAPI } from '../../services/api';
import ScrollMemoryWrapper from '../ScrollMemoryWrapper';
import { HelpWizardProvider } from '../../features/playerHelpWizard/HelpWizardProvider';
import HelpSidebarLink from '../../features/playerHelpWizard/HelpSidebarButton';
import { normalizeWizardTeamContext } from '../../utils/playerTeamWorkspaceHelp';
import LanguageSwitcher from '../Layout/LanguageSwitcher';
import { buildPlayerTeamWorkspacePath, normalizePlayerTeamSection } from '../../utils/playerTeamWorkspace';
import { navigateFromPlayerNotification } from '../../utils/playerNotificationNavigation';
import {
  clearPlayerNotificationBadgeCache,
  invalidatePlayerNotificationQueries,
} from '../../utils/playerNotificationQueries';
import { tryNavigateFromProfileWizard } from '../../utils/incompleteProfileLeaveGuard';
import {
  FaHome,
  FaCalendarAlt,
  FaUsers,
  FaBell,
  FaEnvelope,
  FaCog,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaChartLine,
  FaTrophy,
  FaMedal,
  FaMapMarkerAlt,
  FaUser,
  FaChevronDown,
  FaCamera,
  FaTh,
} from 'react-icons/fa';

function formatShellBadgeCount(n) {
  const x = Number(n) || 0;
  if (x <= 0) return null;
  return x > 99 ? '99+' : String(x);
}

const LAYOUT = {
  sidebarInnerWidth: 260,
  /** Fixed column: left gutters + sidebar card */
  sidebarColumnWidth: 286,
  sidebarGutterY: 14,
  sidebarGutterLeft: 14,
  sidebarGutterRight: 12,
  topBarHeight: 64,
};

const SIDEBAR_BG = '#f3f4f6';

const BRAND = {
  sidebarBlue: '#043663',
  activeRed: '#D52B1E',
  hoverTint: 'rgba(15, 23, 42, 0.06)',
  /** Default shell count badges (nav idle); inverted on red active nav row */
  shellBadgeOnLight: '#D52B1E',
  gray: '#e5e7eb',
  navIdleText: '#0f172a',
  navIdleIcon: '#0f172a',
};

const Container = styled.div`
  min-height: 100vh;
  background: #f9fafb;
  font-family: 'Inter', 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  position: relative;
  overflow-x: hidden;
  ${(p) => (p.$supportBanner ? 'padding-top: 44px;' : '')}
`;

const SupportImpersonationBanner = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.55rem 1rem;
  background: #043663;
  color: #fff;
  font-size: 0.875rem;
  line-height: 1.35;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  span {
    flex: 1;
    min-width: 200px;
  }

  button {
    flex-shrink: 0;
    border: 1px solid rgba(255, 255, 255, 0.55);
    background: transparent;
    color: #fff;
    border-radius: 6px;
    padding: 0.35rem 0.85rem;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
  }

  button:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const SidebarColumn = styled.div`
  position: fixed;
  top: ${(p) => (p.$supportBanner ? '44px' : '0')};
  left: 0;
  height: ${(p) => (p.$supportBanner ? 'calc(100vh - 44px)' : '100vh')};
  width: ${LAYOUT.sidebarColumnWidth}px;
  padding: ${LAYOUT.sidebarGutterY}px ${LAYOUT.sidebarGutterRight}px ${LAYOUT.sidebarGutterY}px ${LAYOUT.sidebarGutterLeft}px;
  box-sizing: border-box;
  z-index: 10;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Sidebar = styled.aside`
  width: 100%;
  height: ${(p) =>
    p.$supportBanner
      ? `calc(100vh - 44px - ${LAYOUT.sidebarGutterY * 2}px)`
      : `calc(100vh - ${LAYOUT.sidebarGutterY * 2}px)`};
  min-height: 0;
  background: ${SIDEBAR_BG};
  padding: 0;
  overflow-y: auto;
  border-radius: 18px;
  box-shadow:
    0 16px 48px rgba(15, 23, 42, 0.12),
    0 6px 16px rgba(15, 23, 42, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
`;

const navBarFillTransition =
  'transform 0.45s cubic-bezier(0.2, 0.85, 0.36, 1), opacity 0.28s cubic-bezier(0.2, 0.85, 0.36, 1)';

const SidebarBrandHeader = styled.div`
  padding: 1.1rem 0.85rem 1rem;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
`;

const BrandBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.45rem;
  margin-bottom: 1rem;
  width: 100%;
`;

const BrandTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  width: 100%;
  padding: 0 0.2rem;
`;

const BrandTitleRule = styled.div`
  width: min(216px, 100%);
  height: 2px;
  align-self: center;
  background: rgba(15, 23, 42, 0.14);
  border-radius: 2px;
`;

const BrandCredit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.55rem;
  width: 100%;
`;

const BrandPoweredBy = styled.a`
  color: #4b5563;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.3;
  text-decoration: none;
  text-transform: none;

  &:hover {
    color: #043663;
    text-decoration: none;
  }
`;

const BrandLogoWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const BrandLogoImage = styled.img`
  height: clamp(52px, 16vw, 80px);
  width: auto;
  max-width: min(200px, 92%);
  object-fit: contain;
  display: block;
`;

const BrandTitle = styled.div`
  color: #0f172a;
  font-size: clamp(0.72rem, 3.2vw, 0.95rem);
  font-weight: 800;
  line-height: 1.35;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  width: 100%;
`;

const ContextSwitcherWrap = styled.div`
  position: relative;
  margin-top: 0;
`;

const ContextSwitcherLabel = styled.div`
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 0.45rem;
`;

const ContextSwitcherTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.72rem 0.75rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: rgba(15, 23, 42, 0.12);
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
  }

  &:focus-visible {
    outline: 2px solid ${BRAND.sidebarBlue};
    outline-offset: 2px;
  }
`;

const ContextSwitcherAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${BRAND.sidebarBlue};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1.05rem;
  overflow: hidden;

  svg {
    width: 17px;
    height: 17px;
  }
`;

/** Wraps team avatar in “Current view” so a logo control can sit on the rim without crowding the trigger. */
const ShellTeamAvatarWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const ShellTeamLogoFab = styled.button`
  position: absolute;
  right: -5px;
  bottom: -5px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: linear-gradient(145deg, #043663, #0c4a7a);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.28);
  z-index: 2;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: scale(1.07);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.38);
  }

  &:focus-visible {
    outline: 2px solid ${BRAND.sidebarBlue};
    outline-offset: 1px;
  }

  svg {
    width: 11px;
    height: 11px;
  }
`;

const ContextSwitcherMenuPickRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  min-width: 0;
`;

const ContextSwitcherRowBadge = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  min-width: 1.2rem;
  height: 1.2rem;
  padding: 0 5px;
  border-radius: 999px;
  background: ${(p) => (p.$invert ? 'rgba(255, 255, 255, 0.96)' : BRAND.shellBadgeOnLight)};
  color: ${(p) => (p.$invert ? BRAND.activeRed : '#fff')};
  box-shadow: ${(p) =>
    p.$invert
      ? '0 1px 3px rgba(15, 23, 42, 0.12)'
      : '0 0 0 1px rgba(0, 0, 0, 0.1)'};
  font-size: 0.65rem;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const ContextSwitcherMenuThumb = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
  background: ${BRAND.sidebarBlue};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;

  svg {
    width: 15px;
    height: 15px;
  }
`;

const ContextSwitcherTextCol = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
`;

const ContextSwitcherPrimary = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContextSwitcherSecondary = styled.div`
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${BRAND.activeRed};
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContextSwitcherChevron = styled(FaChevronDown)`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: #94a3b8;
  transition: transform 0.2s ease;
  transform: rotate(${(p) => (p.$open ? '180deg' : '0')});
`;

const ContextSwitcherMenu = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 6px);
  z-index: 80;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  box-shadow:
    0 12px 36px rgba(15, 23, 42, 0.14),
    0 4px 12px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  padding: 0.35rem 0;
`;

const ContextSwitcherMenuBtn = styled.button`
  width: 100%;
  border: none;
  background: ${(p) => (p.$active ? SIDEBAR_BG : 'transparent')};
  cursor: pointer;
  text-align: left;
  padding: 0.65rem 0.95rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.15rem;
  transition: background 0.12s ease;

  &:hover {
    background: rgba(243, 244, 246, 0.85);
  }
`;

const ContextSwitcherMenuTitle = styled.span`
  font-size: 0.875rem;
  font-weight: ${(p) => (p.$active ? '700' : '600')};
  color: #0f172a;
  line-height: 1.25;
`;

const ContextSwitcherMenuMeta = styled.span`
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${BRAND.activeRed};
`;

const ContextSwitcherTenantLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #0f172a;
  margin: 0.2rem 0.7rem 0;
  padding: 0.45rem 0.25rem 0.35rem;
  line-height: 1.2;
  border-bottom: 2px solid ${BRAND.activeRed};
`;

const MobileMenuBtn = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 10px;
  background: #f1f5f9;
  color: #043663;
  cursor: pointer;
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const DrawerOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${(p) => (p.$open ? 'block' : 'none')};
  }
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 2000;
`;

const DrawerPanel = styled.aside`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
  }
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: min(${LAYOUT.sidebarColumnWidth}px, 92vw);
  background: ${SIDEBAR_BG};
  z-index: 2001;
  transform: translateX(${(p) => (p.$open ? '0' : '-105%')});
  transition: transform 0.22s ease-out;
  box-shadow: ${(p) => (p.$open ? '8px 0 32px rgba(0,0,0,0.2)' : 'none')};
  overflow-y: auto;
`;

const DrawerCloseBtn = styled.button`
  align-self: flex-end;
  margin: 0.65rem 0.85rem 0 0;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.06);
  color: ${BRAND.sidebarBlue};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoIconLarge = styled.div`
  width: clamp(52px, 16vw, 76px);
  height: clamp(52px, 16vw, 76px);
  min-width: 52px;
  background: linear-gradient(145deg, ${BRAND.activeRed}, #b91c1c);
  color: white;
  font-weight: 800;
  font-size: clamp(1.35rem, 5vw, 1.85rem);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
`;

const Nav = styled.nav`
  padding: 1rem 0;
  flex: 1;
`;

/** Stacks above ::before fill so active label stays visible on red bar */
const NavItemLabel = styled.span.attrs({ className: 'nav-item-label' })`
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 0;
  color: inherit;
`;

const NavCountBadge = styled.span`
  margin-left: auto;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 6px;
  border-radius: 999px;
  background: ${BRAND.shellBadgeOnLight};
  color: #fff;
  font-size: 0.68rem;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 1;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12);
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 0.82rem;
  padding: 0.78rem 1rem 0.78rem 1.08rem;
  margin: 0.2rem 0.52rem 0.2rem 0;
  color: ${BRAND.navIdleText};
  text-decoration: none;
  font-size: 1.05rem;
  font-weight: 500;
  position: relative;
  border-radius: 0 999px 999px 0;
  overflow: hidden;
  isolation: isolate;

  & > * {
    position: relative;
    z-index: 1;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${BRAND.activeRed};
    transform: scaleX(0);
    transform-origin: left center;
    z-index: 0;
    opacity: 0;
    transition: ${navBarFillTransition};
    box-shadow: 0 4px 16px rgba(213, 43, 30, 0.4);
  }

  .nav-icon {
    width: 23px;
    height: 23px;
    flex-shrink: 0;
    color: ${BRAND.navIdleIcon};
    transition: color 0.2s ease, opacity 0.2s ease, filter 0.2s ease;
  }

  &:hover::before,
  &.active::before {
    opacity: 1;
    transform: scaleX(1);
  }

  &:hover {
    color: #fff;
    font-weight: 500;
  }

  &.active {
    color: #fff;
    font-weight: 600;
  }

  &:hover .nav-item-label,
  &.active .nav-item-label {
    color: #fff;
  }

  &:hover .nav-icon,
  &.active .nav-icon {
    color: #fff;
    opacity: 1;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.15));
  }

  &.active ${NavCountBadge} {
    background: rgba(255, 255, 255, 0.96);
    color: ${BRAND.activeRed};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.14);
  }

  &:hover:not(.active) ${NavCountBadge} {
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.35);
  }

  &.active:active::before {
    filter: brightness(1.06);
  }
`;

const SidebarFooter = styled.div`
  padding: 1rem 0;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
`;

const LogoutBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.82rem;
  padding: 0.72rem 1.05rem;
  margin: 0.1rem 0.52rem 0 0;
  border: none;
  border-radius: 0 999px 999px 0;
  background: transparent;
  color: ${BRAND.navIdleText};
  font-size: 1.05rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease;
  text-align: left;
  width: calc(100% - 0.52rem);

  .nav-icon {
    width: 23px;
    height: 23px;
    flex-shrink: 0;
    color: ${BRAND.navIdleIcon};
  }

  &:hover {
    background: ${BRAND.hoverTint};
    color: #0f172a;
  }
`;

const FooterSettingsLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 0.82rem;
  padding: 0.78rem 1rem 0.78rem 1.08rem;
  margin: 0.18rem 0.52rem 0.38rem 0;
  color: ${BRAND.navIdleText};
  text-decoration: none;
  font-size: 1.05rem;
  font-weight: 500;
  width: calc(100% - 0.52rem);
  position: relative;
  border-radius: 0 999px 999px 0;
  overflow: hidden;
  isolation: isolate;

  & > * {
    position: relative;
    z-index: 1;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${BRAND.activeRed};
    transform: scaleX(0);
    transform-origin: left center;
    z-index: 0;
    opacity: 0;
    transition: ${navBarFillTransition};
    box-shadow: 0 4px 16px rgba(213, 43, 30, 0.4);
  }

  .nav-icon {
    width: 23px;
    height: 23px;
    flex-shrink: 0;
    color: ${BRAND.navIdleIcon};
    transition: color 0.2s ease, opacity 0.2s ease, filter 0.2s ease;
  }

  &:hover::before,
  &.active::before {
    opacity: 1;
    transform: scaleX(1);
  }

  &:hover {
    color: #fff;
  }

  &.active {
    font-weight: 600;
    color: #fff;
  }

  &:hover .nav-item-label,
  &.active .nav-item-label {
    color: #fff;
  }

  &:hover .nav-icon,
  &.active .nav-icon {
    color: #fff;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.15));
  }
`;

const Main = styled.main`
  flex: 1;
  margin-left: ${LAYOUT.sidebarColumnWidth}px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;

  @media (max-width: 768px) {
    margin-left: 0;
    padding-bottom: 80px;
  }
`;

const TopBar = styled.header`
  height: ${LAYOUT.topBarHeight}px;
  background: #ffffff;
  border-bottom: 1px solid #E5E7EB;
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  position: sticky;
  top: 0;
  z-index: 5;
  min-width: 0;

  @media (max-width: 768px) {
    padding: 0 0.65rem;
    gap: 0.4rem;
  }
`;

const TopBarFill = styled.div`
  flex: 1;
  min-width: 0;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-left: auto;
  min-width: 0;
  flex-shrink: 0;

  @media (max-width: 768px) {
    gap: 0.2rem;
  }
`;

const IconBtn = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  color: #6b7280;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  position: relative;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    background: #f3f4f6;
    color: #1f2937;
  }
`;

const NotifBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  background: ${BRAND.activeRed};
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`;

const BellWrap = styled.div`
  position: relative;
`;
const NotifDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  width: 320px;
  max-height: 400px;
  overflow-y: auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.15);
  border: 1px solid #E5E7EB;
  z-index: 1000;
`;
const NotifDropdownTitle = styled.div`
  padding: 0.75rem 1rem;
  font-weight: 600;
  color: #111;
  border-bottom: 1px solid #E5E7EB;
  font-size: 0.9375rem;
`;
const NotifDropdownToolbar = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
`;
const NotifToolbarBtn = styled.button`
  flex: 1;
  min-width: 0;
  font-size: 0.75rem;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
  color: #0f172a;
  cursor: pointer;
  &:hover {
    background: #eef2f7;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;
const NotifDropdownItem = styled.div`
  border-bottom: 1px solid #f3f4f6;
  background: ${(p) => (p.$unread ? 'rgba(4, 54, 99, 0.04)' : '#fff')};
  font-size: 0.875rem;
`;
const NotifItemRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 2px;
`;
const NotifItemMain = styled.div`
  flex: 1;
  min-width: 0;
  padding: 0.75rem 0.35rem 0.75rem 1rem;
  cursor: pointer;
  &:hover {
    background: #f8fafc;
  }
`;
const NotifDismissBtn = styled.button`
  flex-shrink: 0;
  margin: 0.45rem 0.5rem 0 0;
  border: none;
  background: transparent;
  color: #94a3b8;
  padding: 4px 6px;
  cursor: pointer;
  line-height: 1;
  font-size: 1.1rem;
  &:hover {
    color: #64748b;
  }
`;
const NotifDropdownItemTitle = styled.div`
  font-weight: 600;
  color: #0f172a;
`;
const NotifDropdownItemMsg = styled.div`
  color: #64748b;
  margin-top: 0.2rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const NotifDropdownEmpty = styled.div`
  padding: 1.5rem 1rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.875rem;
`;
const NotifDropdownInboxHint = styled.div`
  padding: 0 1rem 1rem;
  font-size: 0.75rem;
  line-height: 1.45;
  color: #64748b;
  border-top: 1px solid #f3f4f6;
  margin-top: -1px;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  padding: 1.5rem;
  width: 100%;
  position: relative;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const globalLoadingSlide = keyframes`
  0% { transform: translateX(-100%); }
  50% { transform: translateX(350%); }
  100% { transform: translateX(-100%); }
`;

const GlobalLoadingBar = styled.div`
  position: fixed;
  top: 0;
  left: ${LAYOUT.sidebarColumnWidth}px;
  right: 0;
  height: 3px;
  background: rgba(213, 43, 30, 0.2);
  z-index: 9999;
  overflow: hidden;
  pointer-events: none;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 0.2s ease;

  @media (max-width: 768px) {
    left: 0;
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 40%;
    background: ${BRAND.activeRed};
    animation: ${globalLoadingSlide} 1.2s ease-in-out infinite;
    animation-play-state: ${(p) => (p.$visible ? 'running' : 'paused')};
  }
`;

const overlaySpinner = keyframes`
  to { transform: rotate(360deg); }
`;

const NotifDropdownLoader = styled.div`
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  color: #64748b;
  font-size: 0.875rem;
`;
const NotifDropdownSpinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid #e2e8f0;
  border-top-color: ${BRAND.activeRed};
  border-radius: 50%;
  animation: ${overlaySpinner} 0.7s linear infinite;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  pointer-events: ${(p) => (p.$visible ? 'auto' : 'none')};
  transition: opacity 0.25s ease;
`;

const LoadingCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 2rem 2.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  min-width: 260px;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid ${BRAND.gray};
  border-top-color: ${BRAND.activeRed};
  border-radius: 50%;
  animation: ${overlaySpinner} 0.9s linear infinite;
`;

const LoadingText = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  text-align: center;
`;
const LoadingSubtext = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  text-align: center;
`;


function ShellSwitcherThumb({ imageUrl, fallback }) {
  const [failed, setFailed] = React.useState(false);
  if (imageUrl && !failed) {
    return (
      <img
        src={imageUrl}
        alt=""
        onError={() => setFailed(true)}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    );
  }
  return fallback;
}

export default function PlayerDashboardLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const onShellNavIntent = useCallback(
    (e, to, onNavigate) => {
      if (!tryNavigateFromProfileWizard(user, location.pathname, to)) {
        e.preventDefault();
        return;
      }
      onNavigate?.();
    },
    [user, location.pathname]
  );

  const navigateIfProfileAllows = useCallback(
    (path, options) => {
      if (!tryNavigateFromProfileWizard(user, location.pathname, path)) return;
      navigate(path, options);
    },
    [navigate, user, location.pathname]
  );
  const isFetching = useIsFetching();
  // Exclude background/silent refetches from triggering the full-screen overlay (bar still shows for these)
  const isFetchingForOverlay = useIsFetching({
    predicate: (query) => {
      const key = query.queryKey && query.queryKey[0];
      return key !== 'playerNotificationCount';
    }
  });
  const navItems = useMemo(
    () => [
      { to: '/player', end: true, label: t('phase2.playerDashboard.nav.dashboard'), icon: FaHome },
      { to: '/player/tournaments', end: false, label: t('phase2.playerDashboard.nav.tournaments'), icon: FaCalendarAlt },
      { to: '/player/teams', end: false, label: t('phase2.playerDashboard.nav.teams'), icon: FaUsers },
    ],
    [t]
  );
  const displayName = user?.teamName || user?.firstName || t('phase2.playerDashboard.fallbackName');
  const playerFullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || displayName;

  const { data: playerTeamsRes } = useQuery(['playerTeams'], () => playerAPI.getTeams(), {
    staleTime: 60 * 1000,
    enabled: !!user?.playerId,
  });
  const rosterTeams = useMemo(
    () => (Array.isArray(playerTeamsRes?.data) ? playerTeamsRes.data : playerTeamsRes?.data?.data) || [],
    [playerTeamsRes]
  );

  const teamsGroupedByTenant = useMemo(() => {
    const teams = Array.isArray(rosterTeams) ? rosterTeams.filter((x) => x?.teamId) : [];
    if (teams.length === 0) return [];
    const map = new Map();
    for (const team of teams) {
      const tid = team.tenantId != null ? String(team.tenantId) : '_';
      if (!map.has(tid)) {
        map.set(tid, {
          tenantKey: tid,
          tenantId: team.tenantId,
          tenantName: team.tenantName || team.tenant_name || null,
          teams: [],
        });
      }
      map.get(tid).teams.push(team);
    }
    const groups = Array.from(map.values());
    const homeTid = user?.tenantId != null ? String(user.tenantId) : null;
    groups.sort((a, b) => {
      if (homeTid) {
        const ah = a.tenantId != null && String(a.tenantId) === homeTid ? 0 : 1;
        const bh = b.tenantId != null && String(b.tenantId) === homeTid ? 0 : 1;
        if (ah !== bh) return ah - bh;
      }
      return String(a.tenantName || '').localeCompare(String(b.tenantName || ''), undefined, { sensitivity: 'base' });
    });
    for (const g of groups) {
      g.teams.sort((a, b) =>
        String(a.teamName || a.team_name || '').localeCompare(String(b.teamName || b.team_name || ''), undefined, {
          sensitivity: 'base',
        })
      );
    }
    return groups;
  }, [rosterTeams, user?.tenantId]);

  const { data: shellProfileRes } = useQuery(
    ['playerProfileShell'],
    () => playerAPI.getProfile(),
    { enabled: !!user?.playerId, staleTime: 120000 }
  );
  const shellProfilePic = shellProfileRes?.data?.profilePictureUrl || null;

  const teamWorkspaceMatch = useMemo(() => {
    const prefix = '/player/team/';
    if (!location.pathname.startsWith(prefix)) return null;
    const rest = location.pathname.slice(prefix.length);
    const segments = rest.split('/').filter(Boolean);
    if (segments.length < 1) return null;
    return { teamId: segments[0], section: segments[1] || 'overview' };
  }, [location.pathname]);

  const workspaceQs = useMemo(() => {
    const tenant_id = searchParams.get('tenant_id') || '';
    const season_id = searchParams.get('season_id') || '';
    const campaign_id = searchParams.get('campaign_id') || '';
    const o = {};
    if (tenant_id) o.tenant_id = tenant_id;
    if (season_id) o.season_id = season_id;
    if (campaign_id) o.campaign_id = campaign_id;
    return o;
  }, [searchParams]);

  /** While on Settings, keep showing team sidebar if user came from team workspace */
  const [stickyTeamShell, setStickyTeamShell] = React.useState(null);

  React.useEffect(() => {
    const onTeamRoute = location.pathname.startsWith('/player/team/');
    const preserveTeamShellSticky =
      location.pathname.startsWith('/player/settings') ||
      location.pathname.startsWith('/player/league-info') ||
      location.pathname.startsWith('/player/help');
    if (onTeamRoute && teamWorkspaceMatch?.teamId) {
      setStickyTeamShell({
        teamId: String(teamWorkspaceMatch.teamId),
        qs: { ...workspaceQs },
      });
    } else if (!onTeamRoute && !preserveTeamShellSticky) {
      setStickyTeamShell(null);
    }
  }, [location.pathname, teamWorkspaceMatch?.teamId, teamWorkspaceMatch?.section, workspaceQs]);

  const showTeamNav =
    Boolean(teamWorkspaceMatch) ||
    ((location.pathname.startsWith('/player/settings') ||
      location.pathname.startsWith('/player/league-info') ||
      location.pathname.startsWith('/player/help')) &&
      Boolean(stickyTeamShell?.teamId));

  const teamNavCtx =
    teamWorkspaceMatch?.teamId
      ? { teamId: teamWorkspaceMatch.teamId, qs: workspaceQs }
      : stickyTeamShell?.teamId
        ? { teamId: stickyTeamShell.teamId, qs: stickyTeamShell.qs || {} }
        : null;

  /** Only highlight a team nav row when the URL is actually team workspace — not on Settings */
  const activeTeamSectionForHighlight = teamWorkspaceMatch
    ? normalizePlayerTeamSection(teamWorkspaceMatch.section)
    : null;

  const teamSwitchValue =
    teamWorkspaceMatch?.teamId
      ? String(teamWorkspaceMatch.teamId)
      : (location.pathname.startsWith('/player/settings') ||
          location.pathname.startsWith('/player/league-info') ||
          location.pathname.startsWith('/player/help')) &&
          stickyTeamShell?.teamId
        ? String(stickyTeamShell.teamId)
        : '';
  const teamShellNavDefs = useMemo(
    () => [
      { sectionKey: 'overview', label: t('phase2.playerDashboard.teamShell.dashboard'), icon: FaTh },
      { sectionKey: 'stats', label: t('phase2.playerDashboard.teamShell.stats'), icon: FaChartLine },
      { sectionKey: 'results', label: t('phase2.playerDashboard.teamShell.results'), icon: FaTrophy },
      { sectionKey: 'divisions', label: t('phase2.playerDashboard.teamShell.rankings'), icon: FaMedal },
      {
        sectionKey: 'reserve-court',
        label: t('phase2.playerDashboard.teamShell.reserveCourt'),
        icon: FaMapMarkerAlt,
      },
    ],
    [t]
  );

  const navigateToTeamOverview = React.useCallback(
    (team) => {
      if (!team?.teamId || !team.tenantId || !team.latestSeasonId || !team.campaignId) {
        navigateIfProfileAllows('/player/teams');
        return;
      }
      navigateIfProfileAllows(
        buildPlayerTeamWorkspacePath(team.teamId, 'overview', {
          tenant_id: team.tenantId,
          season_id: team.latestSeasonId,
          campaign_id: team.campaignId,
        })
      );
    },
    [navigateIfProfileAllows]
  );



  const [logoError, setLogoError] = React.useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contextSwitcherOpen, setContextSwitcherOpen] = useState(false);
  const contextSwitcherRefDesktop = useRef(null);
  const contextSwitcherRefDrawer = useRef(null);
  const bellRef = useRef(null);
  const teamLogoFileInputRef = useRef(null);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const overlayTimerRef = useRef(null);
  const userInitiatedLoadRef = useRef(false);
  const expireUserInitiatedRef = useRef(null);

  const applyTeamContext = React.useCallback(
    (value, { fromMobileDrawer = false } = {}) => {
      if (fromMobileDrawer) setMobileMenuOpen(false);
      setContextSwitcherOpen(false);
      const v = String(value ?? '');
      if (!v) {
        navigateIfProfileAllows('/player');
        return;
      }
      const row = rosterTeams.find((x) => String(x.teamId) === String(v));
      if (row) navigateToTeamOverview(row);
    },
    [rosterTeams, navigateToTeamOverview, navigateIfProfileAllows]
  );

  const handleTeamWorkspaceLogoUpload = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (event.target) event.target.value = '';
      const tid = String(teamSwitchValue || teamNavCtx?.teamId || '');
      if (!file || !tid) return;

      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/heic',
        'image/heif',
        'image/tiff',
      ];
      const isHeicByExtension = file.name.toLowerCase().endsWith('.heic');
      if (!allowedTypes.includes(file.type.toLowerCase()) && !isHeicByExtension) {
        toast.error(t('teamDashboardPage.toastFileTypeUnsupported', { type: file.type || '' }));
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        toast.error(t('teamDashboardPage.toastFileSize20Mb'));
        return;
      }

      try {
        const result = await teamsAPI.uploadLogo(file, tid);
        if (result?.success) {
          toast.success(t('teamDashboardPage.toastLogoUploaded'));
          await queryClient.invalidateQueries(['playerTeams']);
          await queryClient.invalidateQueries(['dashboardOverview']);
          await queryClient.invalidateQueries(['dashboardSchedule']);
        } else {
          toast.error(result?.message || t('teamDashboardPage.toastLogoUploadFail'));
        }
      } catch (error) {
        const dataMsg = error?.response?.data?.message;
        const msg =
          dataMsg ||
          (error?.message && /too large|413|payload/i.test(error.message)
            ? t('teamDashboardPage.toastImageTooLarge')
            : t('teamDashboardPage.toastLogoUploadFail'));
        toast.error(msg);
      }
    },
    [teamSwitchValue, teamNavCtx?.teamId, queryClient, t]
  );

  // When user navigates (e.g. clicks sidebar), mark that next fetch is user-initiated → show overlay.
  useEffect(() => {
    userInitiatedLoadRef.current = true;
    if (expireUserInitiatedRef.current) window.clearTimeout(expireUserInitiatedRef.current);
    expireUserInitiatedRef.current = window.setTimeout(() => {
      userInitiatedLoadRef.current = false;
      expireUserInitiatedRef.current = null;
    }, 1500);
    return () => {
      if (expireUserInitiatedRef.current) window.clearTimeout(expireUserInitiatedRef.current);
    };
  }, [location.pathname]);

  // Overlay only for user-initiated loads (navigation); background fetches (notification count, version check, etc.) → bar only
  useEffect(() => {
    if (isFetchingForOverlay > 0) {
      if (userInitiatedLoadRef.current) {
        overlayTimerRef.current = window.setTimeout(() => {
          setShowLoadingOverlay(true);
          userInitiatedLoadRef.current = false;
        }, 350);
      }
    } else {
      if (overlayTimerRef.current) {
        window.clearTimeout(overlayTimerRef.current);
        overlayTimerRef.current = null;
      }
      setShowLoadingOverlay(false);
      userInitiatedLoadRef.current = false; // reset so next background fetch doesn't show overlay
    }
    return () => {
      if (overlayTimerRef.current) window.clearTimeout(overlayTimerRef.current);
    };
  }, [isFetching]);

  const { data: countRes } = useQuery(
    ['playerNotificationCount'],
    () => playerAPI.getNotificationCount(),
    { staleTime: 15 * 1000, refetchOnWindowFocus: true, refetchInterval: 20 * 1000 }
  );

  const { data: messagesCountRes } = useQuery(
    ['playerNotificationCount', 'messages'],
    () => playerAPI.getNotificationCount(true),
    { staleTime: 15 * 1000, refetchOnWindowFocus: true, refetchInterval: 20 * 1000 }
  );

  const { data: helpWizardFeaturePayload } = useQuery(
    ['player-help-wizard-feature'],
    () => authAPI.getPlayerHelpWizardFeature(),
    { staleTime: 60 * 1000, retry: 1 }
  );
  const showPlayerHelpWizard = helpWizardFeaturePayload?.data?.enabled === true;

  const wizardStickyTeamId = useMemo(() => {
    if (teamWorkspaceMatch?.teamId) return String(teamWorkspaceMatch.teamId);
    if (
      (location.pathname.startsWith('/player/settings') ||
        location.pathname.startsWith('/player/league-info')) &&
      stickyTeamShell?.teamId
    ) {
      return String(stickyTeamShell.teamId);
    }
    return '';
  }, [teamWorkspaceMatch?.teamId, location.pathname, stickyTeamShell?.teamId]);

  const wizardInitialTeamContext = useMemo(() => {
    if (!wizardStickyTeamId) return null;
    const row = rosterTeams.find(
      (t) => String(t.teamId || t.team_id) === String(wizardStickyTeamId)
    );
    const ctx = normalizeWizardTeamContext(row);
    return ctx ? { ...ctx, rosterRow: row } : null;
  }, [wizardStickyTeamId, rosterTeams]);

  const { data: teamsLeagueEngagement } = useQuery(
    ['playerTeamsLeagueEngagement'],
    async () => {
      const teamsRes = await playerAPI.getTeams();
      const teams = Array.isArray(teamsRes?.data) ? teamsRes.data : [];
      let total = 0;
      const byTeamId = {};
      for (const t of teams) {
        const teamId = t.teamId;
        const seasonId = t.latestSeasonId;
        if (!teamId || !seasonId) continue;
        const extra = t.tenantId ? { headers: { 'X-Tenant-ID': t.tenantId } } : {};
        let u = 0;
        try {
          const ur = await matchChatAPI.getUnreadCounts(t.teamId, t.tenantId);
          u = Number(ur?.totalUnread ?? 0) || 0;
        } catch (e) {
          /* team tenant mismatch or network */
        }
        let p = 0;
        try {
          const ov = await dashboardAPI.getOverview(seasonId, teamId, extra);
          const pv = ov?.data?.pendingScoreVerifications || [];
          for (const x of pv) {
            if (x.submittedBy && x.submittedBy !== teamId) p += 1;
          }
        } catch (e) {
          /* no dashboard context */
        }
        const add = u + p;
        const key = String(teamId);
        byTeamId[key] = (byTeamId[key] || 0) + add;
        total += add;
      }
      return { total, byTeamId };
    },
    { enabled: !!user?.playerId, staleTime: 45 * 1000, refetchInterval: 60 * 1000, refetchOnWindowFocus: true }
  );
  const teamsLeagueCount = Number(teamsLeagueEngagement?.total) || 0;

  const { data: notifUnreadSummaryRes } = useQuery(
    ['playerNotificationsUnreadSummary'],
    () => playerAPI.getNotificationsUnreadSummary(),
    {
      enabled: !!user?.playerId,
      staleTime: 20 * 1000,
      refetchInterval: 45 * 1000,
      refetchOnWindowFocus: true,
    }
  );
  const notifUnreadByTeam = notifUnreadSummaryRes?.data?.byTeamId ?? {};
  const notifUnreadTeamTotal = Number(notifUnreadSummaryRes?.data?.totalTeamUnread) || 0;
  const teamsNavBadgeTotal = teamsLeagueCount + notifUnreadTeamTotal;

  const teamsNavBadgeTitle = useMemo(() => {
    if (teamsNavBadgeTotal <= 0) return '';
    const lines = [];
    for (const team of rosterTeams) {
      const tid = String(team.teamId);
      const eng = Number(teamsLeagueEngagement?.byTeamId?.[tid]) || 0;
      const notif = Number(notifUnreadByTeam[tid]) || 0;
      const n = eng + notif;
      if (n <= 0) continue;
      const name = team.teamName || team.team_name || `Team ${tid.slice(0, 8)}`;
      const parts = [];
      if (notif > 0) parts.push(t('phase2.playerDashboard.notifications.tooltipAlerts', { count: notif }));
      if (eng > 0) parts.push(t('phase2.playerDashboard.notifications.tooltipWorkspace', { count: eng }));
      const detail = parts.length ? parts.join(t('phase2.playerDashboard.notifications.tooltipSep')) : '';
      lines.push(detail ? `${name}: ${n} (${detail})` : `${name}: ${n}`);
    }
    if (lines.length === 0) {
      return t('phase2.playerDashboard.notifications.teamsUnreadTitle', { count: teamsNavBadgeTotal });
    }
    return [t('phase2.playerDashboard.notifications.teamsBadgeTooltipIntro'), ...lines].join('\n');
  }, [rosterTeams, teamsLeagueEngagement, notifUnreadByTeam, teamsNavBadgeTotal, t]);

  const unreadCount = Number(countRes?.data?.unreadCount ?? countRes?.unreadCount) || 0;
  const messagesUnreadCount =
    Number(messagesCountRes?.data?.unreadCount ?? messagesCountRes?.unreadCount) || 0;
  const alertsUnreadCount = Math.max(0, unreadCount - messagesUnreadCount);

  const { data: notifListRes, isLoading: notifListLoading } = useQuery(
    ['playerNotifications'],
    () => playerAPI.getNotifications(),
    { enabled: bellOpen, staleTime: 0 }
  );
  const notifListRaw = Array.isArray(notifListRes?.data) ? notifListRes.data : (notifListRes?.data?.data ?? []);
  const notifList = notifListRaw.filter(
    (n) => n?.type !== 'unread_message' && n?.type !== 'unread_message_reminder'
  );

  const markRead = useMutation(
    (id) => playerAPI.markNotificationRead(id),
    {
      onSuccess: () => invalidatePlayerNotificationQueries(queryClient),
    }
  );

  const markAllReadMutation = useMutation(() => playerAPI.markAllNotificationsRead(), {
    onSuccess: () => {
      clearPlayerNotificationBadgeCache(queryClient);
      invalidatePlayerNotificationQueries(queryClient);
    },
  });

  const clearAllMutation = useMutation(() => playerAPI.clearAllNotifications(), {
    onSuccess: () => {
      clearPlayerNotificationBadgeCache(queryClient);
      invalidatePlayerNotificationQueries(queryClient);
      toast.success(t('phase2.playerDashboard.notifications.toastClearedInbox'));
    },
  });

  const deleteNotifMutation = useMutation((id) => playerAPI.deleteNotification(id), {
    onSuccess: () => invalidatePlayerNotificationQueries(queryClient),
  });

  useEffect(() => {
    const onOutside = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    if (bellOpen) {
      document.addEventListener('click', onOutside);
      return () => document.removeEventListener('click', onOutside);
    }
  }, [bellOpen]);

  React.useEffect(() => {
    if (!contextSwitcherOpen) return undefined;
    const onDocDown = (e) => {
      const inDesktop = contextSwitcherRefDesktop.current?.contains(e.target);
      const inDrawer = contextSwitcherRefDrawer.current?.contains(e.target);
      if (inDesktop || inDrawer) return;
      setContextSwitcherOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setContextSwitcherOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [contextSwitcherOpen]);

  const handleNotifClick = async (n) => {
    if (!n.readAt) markRead.mutate(n.id);
    setBellOpen(false);
    await navigateFromPlayerNotification(n, {
      navigate: (path) => navigate(path),
      navigateIfProfileAllows,
      rosterTeams,
      playerAPIGetTeams: () => playerAPI.getTeams(),
      queryClient,
    });
  };

  const handleNotifHover = (n) => {
    if (!n.readAt) markRead.mutate(n.id);
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const shellBrandHeader = ({ fromDrawer = false, switcherRef } = {}) => {
    const pickerCloseOpts = fromDrawer ? { fromMobileDrawer: true } : {};
    const selectedRosterTeam = teamSwitchValue
      ? rosterTeams.find((x) => String(x.teamId) === String(teamSwitchValue)) || null
      : null;
    const contextSwitcherPrimary = selectedRosterTeam
      ? selectedRosterTeam.team_name || selectedRosterTeam.teamName || `#${teamSwitchValue}`
      : playerFullName || t('phase2.playerDashboard.fallbackName');
    const sidebarBrandTitle = selectedRosterTeam
      ? t('phase2.playerDashboard.brand.sidebarTitleTeam')
      : t('phase2.playerDashboard.brand.sidebarTitle');

    return (
    <SidebarBrandHeader>
      <BrandBlock>
        <BrandLogoWrap>
          {logoError ? (
            <LogoIconLarge>H</LogoIconLarge>
          ) : (
            <BrandLogoImage
              src="/images/logo/logo.png"
              alt={t('phase2.playerDashboard.brand.logoAlt')}
              onError={() => setLogoError(true)}
            />
          )}
        </BrandLogoWrap>
        <BrandTitleBlock>
          <BrandTitle>{sidebarBrandTitle}</BrandTitle>
          <BrandCredit>
            <BrandTitleRule aria-hidden />
            <BrandPoweredBy
              href="https://kamaansolutions.com/ref/helvetic"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('footer.poweredBy')}
            </BrandPoweredBy>
          </BrandCredit>
        </BrandTitleBlock>
      </BrandBlock>
      <ContextSwitcherWrap ref={switcherRef}>
        <ContextSwitcherLabel>{t('phase2.playerDashboard.shell.currentView')}</ContextSwitcherLabel>
        <ContextSwitcherTrigger
          type="button"
          aria-expanded={contextSwitcherOpen}
          aria-haspopup="listbox"
          aria-label={t('phase2.playerDashboard.shell.pickContextAria')}
          onClick={() => setContextSwitcherOpen((o) => !o)}
        >
          {selectedRosterTeam ? (
            <ShellTeamAvatarWrap>
              <ContextSwitcherAvatar>
                <ShellSwitcherThumb
                  imageUrl={selectedRosterTeam.teamLogoUrl || selectedRosterTeam.team_logo_url}
                  fallback={<FaUsers />}
                />
              </ContextSwitcherAvatar>
              <ShellTeamLogoFab
                type="button"
                title={t('phase2.playerDashboard.shell.teamLogoFabTitle')}
                aria-label={t('phase2.playerDashboard.shell.teamLogoFabAria')}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  teamLogoFileInputRef.current?.click();
                }}
              >
                <FaCamera aria-hidden />
              </ShellTeamLogoFab>
            </ShellTeamAvatarWrap>
          ) : (
            <ContextSwitcherAvatar>
              <ShellSwitcherThumb imageUrl={shellProfilePic} fallback={<FaUser />} />
            </ContextSwitcherAvatar>
          )}
          <ContextSwitcherTextCol>
            <ContextSwitcherPrimary>{contextSwitcherPrimary}</ContextSwitcherPrimary>
          </ContextSwitcherTextCol>
          <ContextSwitcherChevron $open={contextSwitcherOpen} aria-hidden />
        </ContextSwitcherTrigger>
        {contextSwitcherOpen && (
          <ContextSwitcherMenu aria-label={t('phase2.playerDashboard.shell.teamSwitcherAria')}>
            {rosterTeams.length > 0 ? (
              <ContextSwitcherTenantLabel>
                {t('phase2.playerDashboard.shell.personalProfileSection')}
              </ContextSwitcherTenantLabel>
            ) : null}
            <ContextSwitcherMenuBtn
              type="button"
              $active={!teamSwitchValue}
              onClick={() => applyTeamContext('', pickerCloseOpts)}
            >
              <ContextSwitcherMenuPickRow>
                <ContextSwitcherMenuThumb>
                  <ShellSwitcherThumb imageUrl={shellProfilePic} fallback={<FaUser />} />
                </ContextSwitcherMenuThumb>
                <div style={{ minWidth: 0 }}>
                  <ContextSwitcherMenuTitle $active={!teamSwitchValue}>
                    {playerFullName || t('phase2.playerDashboard.fallbackName')}
                  </ContextSwitcherMenuTitle>
                </div>
              </ContextSwitcherMenuPickRow>
            </ContextSwitcherMenuBtn>
            {rosterTeams.length > 0
              ? teamsGroupedByTenant.map((group) => (
                  <React.Fragment key={group.tenantKey}>
                    <ContextSwitcherTenantLabel>
                      {group.tenantName || t('phase2.playerDashboard.shell.tenantFallback')}
                    </ContextSwitcherTenantLabel>
                    {group.teams.map((team) => (
                      <ContextSwitcherMenuBtn
                        key={team.teamId}
                        type="button"
                        $active={String(teamSwitchValue) === String(team.teamId)}
                        onClick={() => applyTeamContext(String(team.teamId), pickerCloseOpts)}
                      >
                        <ContextSwitcherMenuPickRow>
                          <ContextSwitcherMenuThumb>
                            <ShellSwitcherThumb
                              imageUrl={team.teamLogoUrl || team.team_logo_url}
                              fallback={<FaUsers />}
                            />
                          </ContextSwitcherMenuThumb>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <ContextSwitcherMenuTitle
                              $active={String(teamSwitchValue) === String(team.teamId)}
                            >
                              {team.team_name || team.teamName || `#${team.teamId}`}
                            </ContextSwitcherMenuTitle>
                          </div>
                          {(() => {
                            const tid = String(team.teamId);
                            const eng = Number(teamsLeagueEngagement?.byTeamId?.[tid]) || 0;
                            const notif = Number(notifUnreadByTeam[tid]) || 0;
                            const shellBadge = eng + notif;
                            const label = formatShellBadgeCount(shellBadge);
                            const teamNm = team.team_name || team.teamName || `#${team.teamId}`;
                            const parts = [];
                            if (notif > 0) {
                              parts.push(t('phase2.playerDashboard.notifications.tooltipAlerts', { count: notif }));
                            }
                            if (eng > 0) {
                              parts.push(
                                t('phase2.playerDashboard.notifications.tooltipWorkspace', { count: eng })
                              );
                            }
                            const titleDetail = parts.join(t('phase2.playerDashboard.notifications.tooltipSep'));
                            const title =
                              label && titleDetail
                                ? `${teamNm}: ${shellBadge} (${titleDetail})`
                                : t('phase2.playerDashboard.notifications.teamsUnreadTitle', {
                                    count: shellBadge,
                                  });
                            return label ? (
                              <ContextSwitcherRowBadge
                                $invert={String(teamSwitchValue) === tid}
                                title={title}
                              >
                                {label}
                              </ContextSwitcherRowBadge>
                            ) : null;
                          })()}
                        </ContextSwitcherMenuPickRow>
                      </ContextSwitcherMenuBtn>
                    ))}
                  </React.Fragment>
                ))
              : null}
          </ContextSwitcherMenu>
        )}
      </ContextSwitcherWrap>
      <input
        ref={teamLogoFileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif,image/tiff,.heic,.heif"
        style={{ display: 'none' }}
        onChange={handleTeamWorkspaceLogoUpload}
      />
    </SidebarBrandHeader>
    );
  };

  const shellBrandHeaderDesktop = shellBrandHeader({ switcherRef: contextSwitcherRefDesktop });
  const shellBrandHeaderDrawer = shellBrandHeader({ fromDrawer: true, switcherRef: contextSwitcherRefDrawer });

  const shellNavFooter = (onNavigate) => (
    <>
      <Nav>
        {showTeamNav && teamNavCtx
          ? teamShellNavDefs.map((def) => {
              if (def.linkTo) {
                const { linkTo, navKey, label, icon: Icon } = def;
                const linkActive =
                  navKey === 'availability'
                    ? location.pathname.startsWith('/player/settings/availability')
                    : false;
                return (
                  <NavItem
                    key={navKey}
                    to={linkTo}
                    end={false}
                    onClick={(e) => onShellNavIntent(e, linkTo, onNavigate)}
                    className={() => (linkActive ? 'active' : undefined)}
                  >
                    <Icon className="nav-icon" />
                    <NavItemLabel>{label}</NavItemLabel>
                  </NavItem>
                );
              }
              const { sectionKey, label, icon: Icon } = def;
              const dest = buildPlayerTeamWorkspacePath(teamNavCtx.teamId, sectionKey, teamNavCtx.qs);
              const isActiveSection =
                activeTeamSectionForHighlight != null &&
                activeTeamSectionForHighlight === normalizePlayerTeamSection(sectionKey);
              return (
                <NavItem
                  key={sectionKey}
                  to={dest}
                  end={false}
                  onClick={(e) => onShellNavIntent(e, dest, onNavigate)}
                >
                  <Icon className="nav-icon" />
                  <NavItemLabel>{label}</NavItemLabel>
                </NavItem>
              );
            })
          : navItems.map(({ to, end, label, icon: Icon }) => (
              <NavItem
                key={to}
                to={to}
                end={end}
                onClick={(e) => onShellNavIntent(e, to, onNavigate)}
                className={({ isActive }) =>
                  isActive ||
                  (to === '/player/tournaments' && location.pathname === '/player/register-team')
                    ? 'active'
                    : undefined
                }
              >
                <Icon className="nav-icon" />
                <NavItemLabel>{label}</NavItemLabel>
                {to === '/player/teams' && teamsNavBadgeTotal > 0 && (
                  <NavCountBadge title={teamsNavBadgeTitle}>
                    {formatShellBadgeCount(teamsNavBadgeTotal)}
                  </NavCountBadge>
                )}
              </NavItem>
            ))}
        {showPlayerHelpWizard ? (
          <HelpSidebarLink
            to="/player/help"
            onNavigate={onNavigate}
            onClick={(e) => onShellNavIntent(e, '/player/help', onNavigate)}
          />
        ) : null}
      </Nav>
      <SidebarFooter>
        {showTeamNav && teamNavCtx && (
          <FooterSettingsLink
            to={buildPlayerTeamWorkspacePath(teamNavCtx.teamId, 'team', teamNavCtx.qs)}
            end={false}
            onClick={(e) =>
              onShellNavIntent(
                e,
                buildPlayerTeamWorkspacePath(teamNavCtx.teamId, 'team', teamNavCtx.qs),
                onNavigate
              )
            }
            className={() =>
              activeTeamSectionForHighlight === 'team' ? 'active' : undefined
            }
          >
            <FaUsers className="nav-icon" />
            <NavItemLabel>{t('phase2.playerDashboard.teamShell.teamMembers')}</NavItemLabel>
          </FooterSettingsLink>
        )}
        <FooterSettingsLink
          to="/player/settings"
          onClick={(e) => onShellNavIntent(e, '/player/settings', onNavigate)}
          className={({ isActive }) =>
            isActive || location.pathname.startsWith('/player/league-info') ? 'active' : undefined
          }
        >
          <FaCog className="nav-icon" />
          <NavItemLabel>{t('phase2.playerDashboard.nav.settings')}</NavItemLabel>
        </FooterSettingsLink>
        <LogoutBtn
          type="button"
          onClick={() => {
            if (!tryNavigateFromProfileWizard(user, location.pathname, '/login')) return;
            onNavigate?.();
            handleLogout();
          }}
        >
          <FaSignOutAlt className="nav-icon" />
          {t('phase2.playerDashboard.logout')}
        </LogoutBtn>
      </SidebarFooter>
    </>
  );

  const shell = (
    <Container $supportBanner={!!user?.impersonation}>
        {user?.impersonation ? (
          <SupportImpersonationBanner role="status">
            <span>
              Support view as {user.email || 'player'} — session ends in about 30 minutes. Password and
              account changes are disabled.
            </span>
            <button
              type="button"
              onClick={() => {
                logout();
                navigate('/login', { replace: true });
              }}
            >
              Exit
            </button>
          </SupportImpersonationBanner>
        ) : null}
        <GlobalLoadingBar $visible={isFetching > 0} aria-hidden="true" />
        <LoadingOverlay $visible={showLoadingOverlay} aria-live="polite" aria-busy={showLoadingOverlay}>
          <LoadingCard>
            <LoadingSpinner aria-hidden="true" />
            <LoadingText>{t('phase2.playerDashboard.loading.title')}</LoadingText>
            <LoadingSubtext>{t('phase2.playerDashboard.loading.subtitle')}</LoadingSubtext>
          </LoadingCard>
        </LoadingOverlay>
        <SidebarColumn $supportBanner={!!user?.impersonation}>
          <Sidebar $supportBanner={!!user?.impersonation}>
            {shellBrandHeaderDesktop}
            {shellNavFooter()}
          </Sidebar>
        </SidebarColumn>

        <DrawerOverlay $open={mobileMenuOpen} onClick={() => setMobileMenuOpen(false)} aria-hidden="true" />
        <DrawerPanel $open={mobileMenuOpen}>
          <DrawerCloseBtn type="button" onClick={() => setMobileMenuOpen(false)} aria-label={t('phase2.playerDashboard.shell.closeMenu')}>
            <FaTimes />
          </DrawerCloseBtn>
          {shellBrandHeaderDrawer}
          {shellNavFooter(() => setMobileMenuOpen(false))}
        </DrawerPanel>

        <Main>
          <TopBar>
            <MobileMenuBtn type="button" aria-label={t('phase2.playerDashboard.shell.openMenu')} onClick={() => setMobileMenuOpen(true)}>
              <FaBars />
            </MobileMenuBtn>
            <TopBarFill aria-hidden="true" />
            <TopBarRight>
              <LanguageSwitcher compact />
              <IconBtn
                type="button"
                onClick={() => navigate('/player/messages')}
                title={t('playerMessagesPage.title')}
                aria-label={t('playerMessagesPage.title')}
              >
                <FaEnvelope />
                {messagesUnreadCount > 0 && (
                  <NotifBadge>{messagesUnreadCount > 99 ? '99+' : messagesUnreadCount}</NotifBadge>
                )}
              </IconBtn>
              <BellWrap ref={bellRef}>
                <IconBtn
                  type="button"
                  onClick={() => setBellOpen((o) => !o)}
                  title={t('phase2.playerDashboard.notifications.bellTitle')}
                >
                  <FaBell />
                  {alertsUnreadCount > 0 && (
                    <NotifBadge>{alertsUnreadCount > 99 ? '99+' : alertsUnreadCount}</NotifBadge>
                  )}
                </IconBtn>
                {bellOpen && (
                  <NotifDropdown>
                    <NotifDropdownTitle>{t('phase2.playerDashboard.notifications.title')}</NotifDropdownTitle>
                    {!notifListLoading && notifList.length > 0 && (
                      <NotifDropdownToolbar>
                        <NotifToolbarBtn
                          type="button"
                          disabled={markAllReadMutation.isLoading}
                          onClick={() => markAllReadMutation.mutate()}
                        >
                          {t('phase2.playerDashboard.notifications.markAllRead')}
                        </NotifToolbarBtn>
                        <NotifToolbarBtn
                          type="button"
                          disabled={clearAllMutation.isLoading}
                          onClick={() => {
                            if (!window.confirm(t('phase2.playerDashboard.notifications.confirmClearAll'))) return;
                            clearAllMutation.mutate();
                          }}
                        >
                          {t('phase2.playerDashboard.notifications.clearAll')}
                        </NotifToolbarBtn>
                      </NotifDropdownToolbar>
                    )}
                    {notifListLoading ? (
                      <NotifDropdownLoader>
                        <NotifDropdownSpinner aria-hidden="true" />
                        {t('phase2.playerDashboard.notifications.loading')}
                      </NotifDropdownLoader>
                    ) : notifList.length === 0 ? (
                      <>
                        <NotifDropdownEmpty>{t('phase2.playerDashboard.notifications.empty')}</NotifDropdownEmpty>
                        {!notifListLoading && teamsNavBadgeTotal > 0 ? (
                          <NotifDropdownInboxHint>
                            {t('phase2.playerDashboard.notifications.emptyInboxTeamHint')}
                          </NotifDropdownInboxHint>
                        ) : null}
                      </>
                    ) : (
                      notifList.map((n) => (
                        <NotifDropdownItem key={n.id} $unread={!n.readAt}>
                          <NotifItemRow>
                            <NotifItemMain
                              role="button"
                              tabIndex={0}
                              onClick={() => handleNotifClick(n)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleNotifClick(n);
                                }
                              }}
                              onMouseEnter={() => handleNotifHover(n)}
                            >
                              <NotifDropdownItemTitle>{n.title}</NotifDropdownItemTitle>
                              <NotifDropdownItemMsg>{n.message}</NotifDropdownItemMsg>
                            </NotifItemMain>
                            <NotifDismissBtn
                              type="button"
                              title={t('phase2.playerDashboard.notifications.dismissOne')}
                              aria-label={t('phase2.playerDashboard.notifications.dismissOne')}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                deleteNotifMutation.mutate(n.id);
                              }}
                            >
                              ×
                            </NotifDismissBtn>
                          </NotifItemRow>
                        </NotifDropdownItem>
                      ))
                    )}
                  </NotifDropdown>
                )}
              </BellWrap>
            </TopBarRight>
          </TopBar>
          <Content>
            <Outlet />
          </Content>
        </Main>
    </Container>
  );

  return (
    <ScrollMemoryWrapper enableScrollMemory>
      {showPlayerHelpWizard ? (
        <HelpWizardProvider rosterTeams={rosterTeams} initialTeamContext={wizardInitialTeamContext}>
          {shell}
        </HelpWizardProvider>
      ) : (
        shell
      )}
    </ScrollMemoryWrapper>
  );
}
